import type { EventCollectionId, SpaceCollectionId } from "./collectionIds";
import type { AdminMode, WorkspaceParams } from "./adminMode";

export const MODE_PREFIX = {
  global: "g",
  account: "a",
  event: "e",
} as const;

export type WorkspaceRouteParams = {
  accountId?: string;
  spaceId?: string;
  eventId?: string;
};

export function getCmsBasePath(mode: AdminMode): string {
  switch (mode.kind) {
    case "hub":
      return "";
    case "global":
      return MODE_PREFIX.global;
    case "account":
      return `${MODE_PREFIX.account}/accounts/${mode.id.accountId}`;
    case "space":
      return `${MODE_PREFIX.account}/accounts/${mode.id.accountId}/spaces/${mode.id.spaceId}`;
    case "event":
      return `${MODE_PREFIX.event}/accounts/${mode.id.accountId}/events/${mode.id.eventId}`;
  }
}

/** Absolute URL prefix for FireCMS `basePath` (must start with `/`). */
export function getCmsBasePathUrl(mode: AdminMode): string {
  const base = getCmsBasePath(mode);
  return base === "" ? "/" : `/${base}`;
}

export function buildHubPath(): string {
  return "/";
}

export function workspaceParamsFromRoute(
  params: WorkspaceRouteParams
): WorkspaceParams {
  return {
    accountId: params.accountId,
    spaceId: params.spaceId,
    eventId: params.eventId,
  };
}

export function buildGlobalPath(): string {
  return `/${getCmsBasePath({ kind: "global" })}`;
}

export function buildAccountPath(accountId: string): string {
  return `/${getCmsBasePath({ kind: "account", id: { accountId } })}`;
}

export function buildSpacePath(id: SpaceCollectionId): string {
  return `/${getCmsBasePath({ kind: "space", id })}`;
}

export function buildEventPath(id: EventCollectionId): string {
  return `/${getCmsBasePath({ kind: "event", id })}`;
}

export function buildPathForWorkspaceParams(params: WorkspaceParams): string {
  if (params.eventId && params.accountId) {
    return buildEventPath({
      accountId: params.accountId,
      spaceId: params.spaceId ?? "",
      eventId: params.eventId,
    });
  }
  if (params.spaceId && params.accountId) {
    return buildSpacePath({
      accountId: params.accountId,
      spaceId: params.spaceId,
    });
  }
  if (params.accountId) {
    return buildAccountPath(params.accountId);
  }
  return buildHubPath();
}
